import React, { useEffect, useRef, useState, useLayoutEffect, useImperativeHandle, forwardRef } from 'react';
import { API, graphqlOperation } from 'aws-amplify';
import { observer } from "mobx-react";
import { getConversationChatAssistant } from '../../../../../graphql/queries';
import { onChatAssistantResponse } from "../../../../../graphql/subscriptions";
import ChatWindowComponent from '../../../../../elements/ChatBox/ChatWindowComponent';
import { ChatInput } from '../../../../../elements/v2/Input';
import _ from "lodash";
import { ChatBoxContainer, ChatMessagesWrapper, ChatInputWrapper } from '../../../../../elements/ChatBox/ChatBox.styled';
import dayjs from 'dayjs'
import { useStore } from '../../../../../hooks';
import { useChatContext } from '../../context/ChatContext';

const ChatBoxPersistent = observer(forwardRef(({ customerSelected, agentId, agentDomain, inputWidth, showNewChatButton, metadata = {}, initialMessage = null, handleMetadata, agentData = {}, onMessagesUpdate = null }, ref) => {
    const { authStore, conversationsStore } = useStore()
    const chatContext = useChatContext();
    
    const conversation = chatContext.getConversation(agentId);
    
    const [newMessage, setNewMessage] = useState('');
    const [selectedAgent, setSelectedAgent] = useState("");
    const [selectedAgentData, setSelectedAgentData] = useState({});
    const [agent_params, setAgent_params] = useState({
        message: "",
        campaign_id: "",
        campaign_version: "",
        agent: ""
    });
    const [attachment, setAttachment] = useState("");
    const subscriptionRef = useRef(null);
    const bottomRef = useRef(null);
    const textAreaRef = useRef();
    customerSelected && conversationsStore.setCustomerId(customerSelected);

    const {
        messages, 
        agentIsTyping, 
        conversationID, 
        showStopButton, 
        isSendMessageClicked,
        hasMessages
    } = conversation;

    useEffect(() => {
        if (onMessagesUpdate) {
            onMessagesUpdate(messages);
        }
    }, [messages, onMessagesUpdate]);

    useEffect(() => {
        if (agentId) {
            setSelectedAgent({ id: agentId, domain: agentDomain });
            setAgent_params((prevParams) => ({
                ...prevParams,
                agent: agentId
            }));
        }
    }, [agentId])

    useEffect(() => {
        if (Object.keys(agentData).length) {
            setSelectedAgentData(agentData)
        }
    }, [agentData])

    useEffect(() => {
        if (agent_params.agent && initialMessage) {
            handleSendMessage(initialMessage, { hideMessage: true })
        }
    }, [agent_params.agent, initialMessage])

    useLayoutEffect(() => {
        const textAreaWrapper = textAreaRef.current;
        if (textAreaWrapper) {
            const textArea = textAreaWrapper.getElementsByTagName('textarea')[0];
            if (textArea) {
                textArea.style.height = "0px";
                const scrollHeight = textArea.scrollHeight;
                textArea.style.height = scrollHeight + "px";
            }
        }
    }, [newMessage]);

    const handleCleanThread = () => {
        chatContext.clearConversation(agentId);
        setAttachment("");
    }

    const setErrorInChat = () => {
        chatContext.setAgentIsTyping(agentId, false);
        const errorMessage = {
            message: "Oops! Something went wrong. Please try again.",
            type: "received",
            time: dayjs().format('HH:mm'),
        };
        chatContext.setMessages(agentId, [...messages, errorMessage]);
        chatContext.setShowStopButton(agentId, false);
    }

    const handleSendMessage = async (sentMessage, hideMessage = false) => {
        if (!sentMessage && newMessage.trim() === "") return;

        chatContext.setIsSendMessageClicked(agentId, true);

        let local_msgs = []
        local_msgs = _.union(messages, [])

        setNewMessage("");

        const params = { ...agent_params,
            message: sentMessage || newMessage,
            customer_id: customerSelected,
            attachment,
            conversation_id: conversationID,
            metadata: JSON.stringify(metadata)
        };

        let openAiAnswer = null;
        try {
            // Activar el indicador de typing antes de hacer el request
            chatContext.setAgentIsTyping(agentId, true);
            
            let resultConversationOpenAi = await API.graphql({
                query: getConversationChatAssistant,
                variables: { input: params },
                authMode: 'AMAZON_COGNITO_USER_POOLS'
            });
            if (!resultConversationOpenAi.errors?.length) {
                const apiResponse = resultConversationOpenAi.data.getConversationChatAssistant;

                const parseResponseBody = (responseBody) => JSON.parse(responseBody);
                if (!hideMessage) {
                    local_msgs = _.union(local_msgs, [{ message: sentMessage || newMessage, type: "sent", time: dayjs().format('HH:mm') }])
                }
                let tmpMsg = "";
                let initialMessages = [...local_msgs];
                let isStreaming = false;
                chatContext.setMessages(agentId, local_msgs);
                setAttachment("");

                // Subscribe to chat assistant response
                subscriptionRef.current = API.graphql({
                    ...graphqlOperation(onChatAssistantResponse, { id: apiResponse.id }),
                    authMode: 'AMAZON_COGNITO_USER_POOLS'
                }).subscribe({
                    next: ({ provider, value }) => {
                        const subApiResponse = value.data.onChatAssistantResponse;
                        const body = parseResponseBody(
                            subApiResponse.body
                        )
                        conversationsStore.setThreadId(body.thread_id);
                        conversationsStore.setRunId(body.run_id);
                        openAiAnswer = body.answer;
                        chatContext.setAgentIsTyping(agentId, false);
                        chatContext.setConversationID(agentId, body.conversation_id);
                        chatContext.setShowStopButton(agentId, true);
                        const { status } = subApiResponse;
                        if (status === 'done' || status === 'guardrail') {
                            chatContext.setShowStopButton(agentId, false);
                            chatContext.setAgentIsTyping(agentId, false);
                            if (!isStreaming) {
                                const newMessages = [...initialMessages, { message: openAiAnswer, type: "received", time: dayjs().format('HH:mm') }];
                                chatContext.setMessages(agentId, newMessages);
                                initialMessages = [...newMessages];
                            }
                            subscriptionRef.current.unsubscribe();
                            tmpMsg = '';
                            initialMessages = [...messages];
                            isStreaming = false;
                            chatContext.setIsSendMessageClicked(agentId, false);
                        } else if (status == 'stream') {
                            isStreaming = true;
                            tmpMsg += openAiAnswer
                            chatContext.setMessages(agentId, [...initialMessages, { message: tmpMsg, type: "received", time: dayjs().format('HH:mm') }]);
                        } else if (status == 'metadata') {
                            handleMetadata && handleMetadata(JSON.parse(body.metadata))
                        }
                    },
                    error: (errors) => {
                        console.error(errors);
                        chatContext.setAgentIsTyping(agentId, false);
                        setErrorInChat();
                        chatContext.setIsSendMessageClicked(agentId, false);
                    }
                });
            } else {
                chatContext.setAgentIsTyping(agentId, false);
                chatContext.setIsSendMessageClicked(agentId, false);
                setErrorInChat();
            }
        } catch (err) {
            chatContext.setAgentIsTyping(agentId, false);
            chatContext.setIsSendMessageClicked(agentId, false);
            setErrorInChat();
        }
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
        // Encontrar el contenedor de mensajes con scroll específico de este chat
        const messagesContainer = document.querySelector(`[style*="overflow: auto"][style*="flex: 1"]`);
        if (messagesContainer) {
            // Solo hacer scroll si el usuario no está scrolleando manualmente
            const isNearBottom = messagesContainer.scrollTop + messagesContainer.clientHeight >= messagesContainer.scrollHeight - 100;
            if (isNearBottom) {
                messagesContainer.scrollTop = messagesContainer.scrollHeight;
            }
        }
    };

    // Exponer la función handleSendMessage al componente padre
    useImperativeHandle(ref, () => ({
        sendMessage: handleSendMessage
    }));

    const handleStopClick = () => {
        if (subscriptionRef.current && (conversationsStore.runId && conversationsStore.threadId && conversationsStore.customerId)) {
            conversationsStore.stopResponse();
            subscriptionRef.current.unsubscribe();
            chatContext.setAgentIsTyping(agentId, false);
            chatContext.setShowStopButton(agentId, false);
            chatContext.setIsSendMessageClicked(agentId, false);
        }
    };

    return (
        <ChatBoxContainer style={{ height: '100vh', maxHeight: '100vh' }}>
            <ChatMessagesWrapper style={{ flex: 1, overflow: 'auto', minHeight: 0, maxHeight: 'calc(100vh - 400px)' }}>
                <ChatWindowComponent
                    selectedAgent={selectedAgentData}
                    agent={selectedAgent}
                    messages={messages}
                    bottomRef={bottomRef}
                    width={'inherit'} height={'100%'} agentIsTyping={agentIsTyping} backgroundColor={'#FAFAFA'} />
            </ChatMessagesWrapper>

            <ChatInputWrapper>
                <ChatInput
                    customerId={authStore.selectedAccount.id}
                    onChange={(e) => setNewMessage(e.target.value)}
                    newMessage={newMessage}
                    handleSendMessage={handleSendMessage}
                    onFileUploaded={setAttachment}
                    textAreaRef={textAreaRef}
                    disableSend={isSendMessageClicked || !newMessage.trim()}
                    onCleanThread={handleCleanThread}
                    showNewChatButton={showNewChatButton}
                    showFileUpload={true}
                    showClearChat={true}
                    stop={showStopButton}
                    onStopClick={handleStopClick}
                    placeholder="Type your instructions here..."
                    maxHeight="150px"
                />
            </ChatInputWrapper>
        </ChatBoxContainer>
    );
}));

export default ChatBoxPersistent; 